import { useState, useEffect, useCallback } from 'react';
import allStore from 'store';
import { useLocation } from 'react-router';
import qs from 'query-string';
import { useApolloClient } from 'react-apollo';

import { CONSENT_COOKIE_CATEGORIES } from '../constants/gdpr';
import { AccountGeolocation } from '../graphql/queries/account-geolocation';

const CONSENT_ACCOUNT_LOCATION_KEY = 'waldo_account_country';
const CONSENT_STORAGE_KEY = 'waldo_gdpr_consent';
const CONSENT_VERSION = '1.0';

/**
 * Consent Manager Hook for GDPR compliance
 *
 * Manages user consent preferences for cookies, tracking, and data processing.
 * Persists consent decisions to localStorage and provides methods to check,
 * save, and withdraw consent.
 *
 * @param {Object} params
 * @param {Boolean} params.fetchAccountGeolocation - Whether or not to fetch fetchAccountGeolocation on load if not available
 * @returns {{
 *   consent: Object|null,
 *   hasConsent: Function,
 *   saveConsent: Function,
 *   withdrawConsent: Function,
 *   isConsentGiven: boolean,
 *   loading: boolean
 * }}
 */
const useConsentManager = ({ fetchAccountGeolocation } = {}) => {
  const [consent, setConsent] = useState(null);
  const [accountGeolocation, setAccountGeolocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAccountGeolocation, setLoadingAccountGeolocation] = useState(
    true,
  );
  const location = useLocation();
  const { gdprConsentGiven, gdprNonEssentialConsentGiven } = qs.parse(
    location.search,
  );
  const client = useApolloClient();

  // Load consent from localStorage on mount.
  // Additionally, if GDPR data is present on the query params, store them as well to avoid showing an unnecessary banner.
  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = allStore.get(CONSENT_STORAGE_KEY);
        if (stored) {
          setConsent(stored);
        } else if (['1', 'true', true].includes(gdprConsentGiven)) {
          const consentData = {
            [CONSENT_COOKIE_CATEGORIES.ESSENTIAL]: true,
            [CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL]: [
              '1',
              'true',
              true,
            ].includes(gdprNonEssentialConsentGiven),
            timestamp: new Date().toISOString(),
            version: CONSENT_VERSION,
          };
          allStore.set(CONSENT_STORAGE_KEY, consentData);
          setConsent(consentData);
        }
      } catch (error) {
        //   Silent fail
      } finally {
        setLoading(false);
      }
    };

    const loadAccountLocation = async () => {
      try {
        const stored = allStore.get(CONSENT_ACCOUNT_LOCATION_KEY);
        if (stored) {
          setAccountGeolocation(stored);
        } else if (fetchAccountGeolocation) {
          const { data } = await client.query({
            query: AccountGeolocation,
            fetchPolicy: 'network-only',
          });
          allStore.set(CONSENT_ACCOUNT_LOCATION_KEY, data.accountGeolocation);
          setAccountGeolocation(data.accountGeolocation);
        }
      } catch (error) {
        //   Silent fail
      } finally {
        setLoadingAccountGeolocation(false);
      }
    };

    loadConsent();
    loadAccountLocation();
  }, []);

  /**
   * Check if user has given consent for a specific category
   *
   * @param {string} category - One of CONSENT_CATEGORIES
   * @returns {boolean} - True if consent was given for this category
   */
  const hasConsent = useCallback(
    category => {
      if (!consent) return false;
      return consent[category] === true;
    },
    [consent],
  );

  /**
   * Save user consent preferences
   *
   * @param {Object} preferences - Object with consent categories as keys and boolean values
   * @param {boolean} preferences.necessary - Always true, required for site functionality
   * @param {boolean} preferences.analytics - Consent for analytics tracking
   * @param {boolean} preferences.marketing - Consent for marketing cookies
   * @param {boolean} preferences.facialRecognition - Consent for facial recognition processing
   * @returns {Object} - The saved consent object with timestamp and version
   */
  const saveConsent = useCallback(preferences => {
    try {
      const consentData = {
        ...preferences,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION,
      };

      allStore.set(CONSENT_STORAGE_KEY, consentData);
      setConsent(consentData);

      return consentData;
    } catch (error) {
      //   Silent fail
      return null;
    }
  }, []);

  /**
   * Withdraw all consent and clear stored preferences
   * This will require the user to provide consent again
   */
  const withdrawConsent = useCallback(() => {
    try {
      allStore.remove(CONSENT_STORAGE_KEY);
      setConsent(null);
    } catch (error) {
      //  Silent fail
    }
  }, []);

  return {
    consent,
    hasConsent,
    saveConsent,
    withdrawConsent,
    loading,
    accountGeolocation,
    loadingAccountGeolocation,
  };
};

export default useConsentManager;
